import { lazy, Suspense, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import DocumentService from '../services/DocumentService';
import { Alert, Button, Card, Col, Row, Space, Spin } from 'antd';
import { IDocumentResponse } from '../types/Documents';
import { PDFDocument, PDFFont, StandardFonts, rgb, degrees } from 'pdf-lib';
import { turkishToEnglish } from '../utils';

const PdfViewer = lazy(() => import('../components/Documents/PdfViewer'));

export const isMobileOrTabletDevice = (
    userAgent: string,
    platform: string,
    maxTouchPoints: number,
    hasCoarsePointer: boolean,
    viewportWidth: number,
) => (
    /Android|iPad|iPhone|iPod|Mobile|Tablet/i.test(userAgent)
    || (platform === 'MacIntel' && maxTouchPoints > 1)
    || (hasCoarsePointer && maxTouchPoints > 0 && viewportWidth <= 1366)
);

export const revokeGeneratedPdfUrl = (pdfUrl?: string) => {
    if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
    }
};

export const createDocumentDownloadFileName = (title?: string) => {
    const safeTitle = (title || 'dokuman')
        .replace(/\.pdf$/i, '')
        .replace(/[\\/:*?"<>|]+/g, '-')
        .trim() || 'dokuman';

    return `${safeTitle}-filigranli.pdf`;
};

export const createDocumentWatermarkText = (
    firstName: string,
    lastName: string,
    email: string,
) => `${turkishToEnglish(firstName)} ${turkishToEnglish(lastName)}\n${email}`;

interface WatermarkPlacement {
    x: number;
    y: number;
    size: number;
    lineHeight: number;
}

interface WatermarkBounds {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

const WATERMARK_ANGLE = 40;
const WATERMARK_COLUMNS = 2;
const WATERMARK_ROWS = 4;
const WATERMARK_MAX_FONT_SIZE = 25;
const WATERMARK_LINE_HEIGHT_RATIO = 1.2;

const getRotatedWatermarkBounds = (
    text: string,
    font: PDFFont,
    placement: WatermarkPlacement,
): WatermarkBounds => {
    const lines = text.split('\n');
    const textWidth = Math.max(
        ...lines.map((line) => font.widthOfTextAtSize(line, placement.size)),
        0.01,
    );
    const ascenderHeight = font.heightAtSize(placement.size, { descender: false });
    const fontHeight = font.heightAtSize(placement.size);
    const descenderHeight = Math.max(fontHeight - ascenderHeight, 0);
    const localMinY = -((lines.length - 1) * placement.lineHeight) - descenderHeight;
    const localMaxY = ascenderHeight;
    const radians = WATERMARK_ANGLE * Math.PI / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const corners = [
        { x: 0, y: localMinY },
        { x: textWidth, y: localMinY },
        { x: 0, y: localMaxY },
        { x: textWidth, y: localMaxY },
    ].map(({ x, y }) => ({
        x: placement.x + x * cos - y * sin,
        y: placement.y + x * sin + y * cos,
    }));

    return {
        minX: Math.min(...corners.map(({ x }) => x)),
        maxX: Math.max(...corners.map(({ x }) => x)),
        minY: Math.min(...corners.map(({ y }) => y)),
        maxY: Math.max(...corners.map(({ y }) => y)),
    };
};

export const createWatermarkPlacements = (
    pageWidth: number,
    pageHeight: number,
    text: string,
    font: PDFFont,
): WatermarkPlacement[] => {
    const pageMargin = Math.min(12, pageWidth * 0.04, pageHeight * 0.04);
    const cellGap = Math.min(8, pageWidth * 0.02, pageHeight * 0.02);
    const cellWidth = (
        pageWidth - pageMargin * 2 - cellGap * (WATERMARK_COLUMNS - 1)
    ) / WATERMARK_COLUMNS;
    const cellHeight = (
        pageHeight - pageMargin * 2 - cellGap * (WATERMARK_ROWS - 1)
    ) / WATERMARK_ROWS;
    const cellPadding = Math.min(4, cellWidth * 0.05, cellHeight * 0.05);
    const unitPlacement = {
        x: 0,
        y: 0,
        size: 1,
        lineHeight: WATERMARK_LINE_HEIGHT_RATIO,
    };
    const unitBounds = getRotatedWatermarkBounds(text, font, unitPlacement);
    const unitWidth = unitBounds.maxX - unitBounds.minX;
    const unitHeight = unitBounds.maxY - unitBounds.minY;
    const fontSize = Math.min(
        WATERMARK_MAX_FONT_SIZE,
        Math.max((cellWidth - cellPadding * 2) / unitWidth, Number.EPSILON),
        Math.max((cellHeight - cellPadding * 2) / unitHeight, Number.EPSILON),
    );
    const lineHeight = fontSize * WATERMARK_LINE_HEIGHT_RATIO;
    const scaledBounds = getRotatedWatermarkBounds(text, font, {
        x: 0,
        y: 0,
        size: fontSize,
        lineHeight,
    });
    const watermarkWidth = scaledBounds.maxX - scaledBounds.minX;
    const watermarkHeight = scaledBounds.maxY - scaledBounds.minY;
    const placements: WatermarkPlacement[] = [];

    for (let row = 0; row < WATERMARK_ROWS; row += 1) {
        for (let column = 0; column < WATERMARK_COLUMNS; column += 1) {
            const cellLeft = pageMargin + column * (cellWidth + cellGap);
            const cellBottom = pageMargin + row * (cellHeight + cellGap);

            placements.push({
                x: cellLeft + (cellWidth - watermarkWidth) / 2 - scaledBounds.minX,
                y: cellBottom + (cellHeight - watermarkHeight) / 2 - scaledBounds.minY,
                size: fontSize,
                lineHeight,
            });
        }
    }

    return placements;
};

export const getWatermarkBounds = getRotatedWatermarkBounds;

const Document = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [document, setDocument] = useState<IDocumentResponse>();
    const [pdfUrl, setPdfUrl] = useState<string>('');
    const [pdfFile, setPdfFile] = useState<Blob | null>(null);
    const [error, setError] = useState<string>('');
    const [usesEmbeddedPdfViewer] = useState(() => isMobileOrTabletDevice(
        window.navigator.userAgent,
        window.navigator.platform,
        window.navigator.maxTouchPoints,
        window.matchMedia?.('(pointer: coarse)').matches ?? false,
        window.innerWidth,
    ));
    const { id } = useParams();

    useEffect(() => {
        let isActive = true;
        let generatedPdfUrl: string | undefined;

        const loadDocument = async () => {
            setLoading(true);
            setError('');
            setPdfUrl('');
            setPdfFile(null);

            try {
                const documentResponse: any = await DocumentService.get(id);
                const documentData = documentResponse.data;

                if (!isActive) return;

                setDocument(documentData);
                const fileResponse: any = await DocumentService.getFile(documentData?.url);
                const arrayBuffer = await fileResponse.data.arrayBuffer();
                const pdfDoc = await PDFDocument.load(arrayBuffer);
                const watermarkFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
                const text = createDocumentWatermarkText(
                    localStorage.getItem('firstName') || '',
                    localStorage.getItem('lastName') || '',
                    localStorage.getItem('email') || '',
                );

                pdfDoc.getPages().forEach((page) => {
                    const { width, height } = page.getSize();
                    const placements = createWatermarkPlacements(
                        width,
                        height,
                        text,
                        watermarkFont,
                    );

                    placements.forEach(({ x, y, size, lineHeight }) => {
                        page.drawText(text, {
                            x,
                            y,
                            size,
                            lineHeight,
                            font: watermarkFont,
                            color: rgb(0.75, 0.75, 0.75),
                            rotate: degrees(WATERMARK_ANGLE),
                            opacity: 0.7,
                        });
                    });
                });

                const newPdfBytes = await pdfDoc.save();
                const generatedPdfFile = new Blob([newPdfBytes], { type: 'application/pdf' });
                generatedPdfUrl = URL.createObjectURL(generatedPdfFile);

                if (!isActive) {
                    revokeGeneratedPdfUrl(generatedPdfUrl);
                    return;
                }

                setLoading(false);
                setPdfUrl(generatedPdfUrl);
                setPdfFile(generatedPdfFile);
            } catch (_error) {
                if (!isActive) return;

                setError('Doküman hazırlanırken bir sorun oluştu. Lütfen tekrar deneyin.');
                setLoading(false);
            }
        };

        loadDocument();

        return () => {
            isActive = false;
            revokeGeneratedPdfUrl(generatedPdfUrl);
        };
    }, [id]);

    const downloadFileName = createDocumentDownloadFileName(document?.title);

    return (
        <>
            <Row justify="space-between">
                <Col>
                    <h3>{document?.title}</h3>
                </Col>
            </Row>
            <Card loading={loading}>
                {error && <Alert message="Doküman görüntülenemedi" description={error} type="error" showIcon />}
                {!usesEmbeddedPdfViewer && pdfUrl && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Button
                            type="primary"
                            href={pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Ayrı sekmede aç
                        </Button>
                        <iframe
                            title="Document"
                            src={pdfUrl}
                            width="100%"
                            height="900"
                        />
                    </Space>
                )}
                {usesEmbeddedPdfViewer && pdfFile && (
                    <Suspense fallback={<div className="document-pdf-viewer__status"><Spin /></div>}>
                        <PdfViewer file={pdfFile} fileName={downloadFileName} />
                    </Suspense>
                )}
            </Card>
        </>
    )
}

export default Document
